import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import * as queryString from 'query-string';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { geocodeByAddress, getLatLng } from 'react-places-autocomplete';

import { fetchPrismicContent } from '../../../../actions/search-actions';
import { getCurrentBrowserLocation } from './utils/locationHelpers';
import EventFilters from './components/EventFilters';
import EventsList from './components/EventsList';
import LoadMoreButton from './components/LoadMoreButton';

import { useEventSearch } from './hooks/useEventSearch';
import { useEventFilters } from './hooks/useEventFilters';
import { useNaturalSearchBridge } from './hooks/useNaturalSearchBridge';
import { determineInitialFilters } from './utils/eventSearchHelpers';

import { useAdDynamicConfig } from '../../../../components/hooks/useAdDynamicConfig';
import useIsMobile from '../../../../components/hooks/useIsMobile';
import { didNaturalSearchTransition } from '../../../../utils/naturalSearch';

import styles from '../searchV2.scss';
import { FILTER_KEYS, FILTER_VALUES, QUERY_BEHAVIOR } from '../constants';

const { EVENTS, DEFAULT_DISTANCE } = FILTER_VALUES;
const {
  TYPE,
  SPORT_TYPES,
  TOURS,
  EVENT_TYPES,
  EVENT_TEAM_TYPES,
  GENDERS,
  BALL_TYPES,
  EVENT_UTR_TYPES,
  QUERY,
  LOCATION_INPUT_VALUE,
} = FILTER_KEYS;

const EventSearchResults = () => {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const auth = useSelector(state => state.auth);
  const coreUI = useSelector(state => state.coreUI);
  const search = useSelector(state => state.search);
  const { isMobileWidth, isTabletWidth } = useIsMobile();
  const { showAds, eventSearchConfig } = useAdDynamicConfig();

  // Parse URL parameters
  const parsed = queryString.parse(location.search);
  const hasQueryParam = Object.keys(parsed).length > 0 && parsed?.query;
  // Initialize filters from URL
  const initialFilters = determineInitialFilters(parsed, auth);
  const submitType = 'submit';
  const resultTypeCountry = 'country';
  const resultTypeAdministrativeAreaLevel1 = 'administrative_area_level_1';
  // Custom hooks for state management
  const {
    events,
    loading,
    error,
    pageLoadedOnce,
    searchEvents,
    loadMoreEvents,
    isLoadingMore,
  } = useEventSearch();

  const {
    filters,
    locationInputValue,
    pendingLocationData,
    updateFilters,
    updateLocationInput,
    setPendingLocation,
    applyPendingLocation,
    replaceFilters,
    clearFilters,
  } = useEventFilters(initialFilters);

  // Ensure prismic content is fetched
  useEffect(() => {
    if (!search.prismicContentLoaded && !search.isLoading) {
      dispatch(fetchPrismicContent());
    }
  }, [dispatch, search.prismicContentLoaded, search.isLoading]);

  const naturalSearch = useSelector(s => s.naturalSearch);
  const lastSeenNlSentinelRef = useRef(naturalSearch?.appliedSentinel ?? 0);

  // Initialize search on component mount.
  // Skip if an NL search just landed (appliedSentinel > 0 at mount time) — the
  // bridge effect below will run the search with LLM-derived filters; firing
  // here too races and the URL-derived defaults can win.
  useEffect(() => {
    if (lastSeenNlSentinelRef.current > 0) {
      return;
    }
    const performInitialSearch = async () => {
      if (filters.type === EVENTS) {
        await searchEvents(filters, parsed.query);
      }
    };
    performInitialSearch();
  }, []);

  // Update URL when filters change
  useEffect(() => {
    updateUrlParams();
  }, [filters]);

  // React to changes in the query param (e.g., from GlobalSearch "See All").
  // Skip the URL->filter rehydrate when the URL change came from an NL apply,
  // otherwise we clobber the bridged LLM filters with whatever
  // determineInitialFilters can recover from the URL.
  const lastQueryRef = useRef(parsed.query || '');
  useEffect(() => {
    const currentParsed = queryString.parse(location.search);
    const currentQuery = currentParsed.query || '';
    if (currentQuery !== lastQueryRef.current) {
      lastQueryRef.current = currentQuery;
      const nlSentinel = naturalSearch?.appliedSentinel ?? 0;
      if (didNaturalSearchTransition(lastSeenNlSentinelRef.current, nlSentinel)) {
        lastSeenNlSentinelRef.current = nlSentinel;
        return;
      }
      const nextFilters = determineInitialFilters(currentParsed, auth);
      replaceFilters(nextFilters);
      searchEvents(nextFilters, currentQuery);
    }
  }, [location.search]);

  useNaturalSearchBridge({
    naturalSearch,
    searchFilters: search.filters,
    replaceFilters,
    searchEvents,
  });

  const updateUrlParams = () => {
    const params = new URLSearchParams();
    const queryParams = [];
    // Add filters to URL
    Object.entries(filters).forEach(([key, value]) => {
      // Skip if value is falsy, empty, null, or if it's a field we want to exclude with query
      if (!value || value === '' || value === null || key === TYPE || key === LOCATION_INPUT_VALUE) {
        return;
      }

      // If there's a query parameter, exclude certain fields
      if (hasQueryParam && QUERY_BEHAVIOR.FIELDS_EXCLUDED_FROM_URL.includes(key)) {
        return;
      }

      if (typeof value === 'object' && value._isAMomentObject) {
        params.set(key, value.format('MM/DD/YYYY'));
      } else if (typeof value === 'object' && !Array.isArray(value)) {
          const activeValues = Object.entries(value)
            .filter(([, isActive]) => isActive)
            .map(([val]) => val);
          if (activeValues.length > 0) {
            // Special handling for filters that need comma-separated values with %20 encoding
            const individualParamFilters = [SPORT_TYPES, TOURS, EVENT_TYPES, EVENT_TEAM_TYPES, GENDERS, BALL_TYPES, EVENT_UTR_TYPES];
            if (individualParamFilters.includes(key)) {
              queryParams.push(`${key}=${activeValues.join(',')}`);
            } else {
              params.set(key, activeValues.join(','));
            }
          }
        } else {
          params.set(key, value);
        }
    });

    // Add query if present
    if (parsed.query) {
      params.set(QUERY, parsed.query);
    }

    // Add type
    params.set(TYPE, EVENTS);

    // Combine URLSearchParams with manual query parameters
    const urlSearchParamsString = params.toString();
    const manualParamsString = queryParams.join('&');

    let finalUrl = urlSearchParamsString;
    if (manualParamsString) {
      finalUrl = finalUrl ? `${finalUrl}&${manualParamsString}` : manualParamsString;
    }

    const currentUrl = location.search.replace('?', '');

    if (finalUrl !== currentUrl) {
      history.replace({
        pathname: location.pathname,
        search: finalUrl ? `?${finalUrl}` : '',
      });
    }
  };

  const handleFilterUpdate = (newFilters, updateType = submitType) => {
    // Simplified: always update filters and optionally search
    updateFilters(newFilters);

    if (updateType === submitType) {
      searchEvents({ ...filters, ...newFilters }, parsed.query);
    }
  };

  const handleFilterSubmit = (updatedFilters) => {
    const filtersToUse = updatedFilters || filters;
    searchEvents(filtersToUse, parsed.query);
  };

  const handleClearFilters = () => {
    const clearedFilters = clearFilters();
    searchEvents(clearedFilters, parsed.query);
  };

  const handleLoadMore = () => {
    loadMoreEvents(filters, parsed.query);
  };

const handleCurrentLocationClick = async () => {
  await getCurrentBrowserLocation({
    auth,
    filters,
    updateLocationInput,
    setPendingLocation,
  });
};

  const applyLocationFilter = () => {
    if (!locationInputValue || locationInputValue.trim() === '') {
      // If locationInputValue is empty, clear all location filters
      const clearedLocationFilters = {
        location: null,
        lat: null,
        lng: null,
        distance: null,
        locationCountryName: null,
        locationStateName: null,
      };
      handleFilterUpdate(clearedLocationFilters, submitType);
      return;
    }

    if (pendingLocationData) {
      // We have geocoded data ready to apply
      const updatedFilters = applyPendingLocation();
      handleFilterSubmit(updatedFilters);
    } else {
      // Need to geocode the location input (user typed manually)
      geocodeByAddress(locationInputValue)
        .then(async (results) => {
          const latLng = await getLatLng(results[0]);
          return { results, latLng };
        })
        .then(({ results, latLng }) => {
          const result = results[0];
          const resultType = result.types[0];

          let locationFilters;

          if (resultType === resultTypeCountry) {
            // Country-level search
            locationFilters = {
              locationCountryName: result.address_components[0].long_name,
              locationStateName: '',
              distance: filters.distance || DEFAULT_DISTANCE,
              location: '',
              lat: null,
              lng: null,
            };
          } else if (resultType === resultTypeAdministrativeAreaLevel1) {
            // State/province-level search
            locationFilters = {
              locationCountryName: result.address_components[1].long_name,
              locationStateName: result.address_components[0].long_name,
              distance: filters.distance || DEFAULT_DISTANCE,
              location: '',
              lat: null,
              lng: null,
            };
          } else {
            // City/specific location - use coordinates
            locationFilters = {
              lat: latLng.lat,
              lng: latLng.lng,
              location: locationInputValue,
              distance: filters.distance || DEFAULT_DISTANCE,
              locationCountryName: '',
              locationStateName: '',
            };
          }

          handleFilterUpdate(locationFilters, submitType);
        })
        .catch(error => {
          console.error('geocodeByAddress error', error);
        });
    }
  };

  const eventFiltersProps = {
    filters,
    locationInputValue,
    updateFilters,
    onFilterSubmit: handleFilterSubmit,
    onClearFilters: handleClearFilters,
    auth,
    userLoggedIn: !!auth?.user,
    eventCount: events.total || 0,
    hasQueryParam,
    searchTypes: coreUI?.types?.search || {},
    onLocationChange: (value) => {
      updateLocationInput(value);
    },
    onLocationSelect: (location) => {
      if (location) {
        // Update the input value immediately for UI feedback
        updateLocationInput(location);

        // Geocode and prepare the location data (but don't apply yet)
        geocodeByAddress(location)
        .then(async (results) => {
          const latLng = await getLatLng(results[0]);
          return { results, latLng };
        })
        .then(({ results, latLng }) => {
          const result = results[0];
          const resultType = result.types[0];

          let locationFilters;

          if (resultType === resultTypeCountry) {
            // Country-level search
            locationFilters = {
              locationCountryName: result.address_components[0].long_name,
              locationStateName: '',
              location: '',
              lat: null,
              lng: null,
            };
          } else if (resultType === resultTypeAdministrativeAreaLevel1) {
            // State/province-level search
            locationFilters = {
              locationCountryName: result.address_components[1].long_name,
              locationStateName: result.address_components[0].long_name,
              location: '',
              lat: null,
              lng: null,
            };
          } else {
            // City/specific location - use coordinates
            locationFilters = {
              lat: latLng.lat,
              lng: latLng.lng,
              location,
              locationCountryName: '',
              locationStateName: '',
            };
          }

          // Store the geocoded data as pending (don't apply to button yet)
          setPendingLocation(locationFilters);
        })
        .catch(error => {
          console.error('geocodeByAddress error', error);
        });
      }
    },
    onCurrentLocationClick: handleCurrentLocationClick,
    onDistanceChange: (distance) => {
      updateFilters({ distance: `${distance}mi` });
    },
    onApplyLocationFilter: () => {
      applyLocationFilter();
    },
  };

  const shouldShowLoadMore = events.hits && events.hits.length < Math.min(events.total, events.totalAllowed || Infinity);

  const getNoEventsMessage = () => {
    const hasLocation = filters.location !== null && filters.location !== undefined && filters.location !== '';
    const hasFilters = Object.keys(filters).length > 1;

    if (!hasLocation && !hasFilters) {
      return 'Please select your location to find events near you.';
    }

    if (hasFilters) {
      return 'There are no events that meet this criteria in this location.';
    }

    return 'No events found in this location.';
  };

  return (
      <div className={`container ${styles.search} mb56`}>
          <EventFilters {...eventFiltersProps} />
          {loading && !pageLoadedOnce && (
          <div className={styles.emptypage} data-testid="search.events.results.loading">
              <i className="fas fa-circle-notch fa-spin" />&nbsp;Loading...
          </div>
      )}
          {!loading && pageLoadedOnce && (!events.hits || events.hits.length === 0) && (
          <div className={styles.emptypage} data-testid="search.events.results.empty">
              <p className="mt48 text-center" data-testid="search.events.results.empty.message">{getNoEventsMessage()}</p>
          </div>
      )}
          {events.hits && events.hits.length > 0 && (
          <div>
              <EventsList
                  events={events.hits}
                  auth={auth}
                  eventSearchConfig={eventSearchConfig}
                  shouldShowTwoRectangleAds={isMobileWidth || isTabletWidth}
                  showAds={showAds}
              />
              {shouldShowLoadMore && (
              <LoadMoreButton
                  onLoadMore={handleLoadMore}
                  isLoading={isLoadingMore}
                  data-testid="search.events.load-more-btn"
              />
            )}
          </div>
      )}
          {error && (
          <div className={styles.emptypage} data-testid="search.events.results.error">
              <p data-testid="search.events.results.error.message">Something went wrong. Please try again.</p>
          </div>
      )}
      </div>
  );
};

EventSearchResults.propTypes = {
  eventSearchConfig: PropTypes.instanceOf(Object),
  showAds: PropTypes.bool,
};

EventSearchResults.defaultProps = {
  eventSearchConfig: {},
  showAds: false,
};

export default EventSearchResults;
